import React, { useMemo } from 'react';
import { Newspaper, Plus } from 'lucide-react';
import { C3_BRIEF_HTML } from '../../mock/c3';
import { C3_INTEL_SECTIONS } from '../../mock/c3/intel';
import { intelAge, rankBySeverity, type IntelSeverity } from '../../lib/intel';
import SidePanel from '../../ui/SidePanel';

interface IntelligenceBriefSlideOverProps {
  open: boolean;
  onClose: () => void;
  onCiteIncident: (id: string) => void;
  /** Starts connecting a source of threat intelligence. */
  onConnectSource: () => void;
}

const SEVERITY_WORD: Record<IntelSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Threat Intel (it was the Intelligence Brief): the day's narrative, then one section per source, each laid out the same
 * way, with the source's title and its items ranked by severity (client notes 10/26, Threat Intel 1 to 3). The sections are
 * sample data until sources of intelligence can be connected.
 */
const IntelligenceBriefSlideOver: React.FC<IntelligenceBriefSlideOverProps> = ({
  open,
  onClose,
  onCiteIncident,
  onConnectSource,
}) => {
  // The brief is HTML from the mock; one handler at the container reads the incident from the cite that was hit.
  const onBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const id = (e.target as HTMLElement).closest<HTMLElement>('.cite')?.getAttribute('data-inc');
    if (id) onCiteIncident(id);
  };
  const sections = useMemo(
    () => C3_INTEL_SECTIONS.map((section) => ({ ...section, items: rankBySeverity(section.items) })),
    []
  );

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Threat Intel"
      subtitle="Trailing 24h · correlated narrative"
      icon={<Newspaper size={16} strokeWidth={1.5} aria-hidden className="text-c3-purple" />}
      closeLabel="Close Threat Intel"
      actions={
        <button type="button" className="c3-btn c3-btn-sm inline-flex items-center gap-2" onClick={onConnectSource}>
          <Plus size={16} strokeWidth={1.5} aria-hidden />
          Connect a source
        </button>
      }
    >
      <div className="px-5 py-[18px] overflow-y-auto text-sm leading-relaxed">
        <div onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: C3_BRIEF_HTML }} />
        <p className="c3-intel-note">Sample data. Connect a source to read your own.</p>
        {sections.map((section) => (
          <section key={section.name} className="c3-intel-section" aria-label={section.name}>
            <h4 className="c3-intel-title">{section.name}</h4>
            <ul className="c3-intel-list">
              {section.items.map((item) => (
                <li key={item.id} className="c3-intel-item" data-sev={item.severity}>
                  <div className="c3-intel-head">
                    <span className="c3-intel-sev">{SEVERITY_WORD[item.severity]}</span>
                    <span className="c3-intel-age">{intelAge(item.minutesAgo)}</span>
                  </div>
                  <div className="c3-intel-item-title">{item.title}</div>
                  <p className="c3-intel-detail">{item.detail}</p>
                  {item.incident ? (
                    <button type="button" className="c3-intel-inc" onClick={() => onCiteIncident(item.incident as string)}>
                      Bears on {item.incident}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </SidePanel>
  );
};

export default IntelligenceBriefSlideOver;
